import { useState, useEffect } from 'react';
import { DndContext, DragOverlay, useSensor, useSensors, PointerSensor } from '@dnd-kit/core';
import { format, addDays, startOfWeek, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, parseISO } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { employeeAPI, workOrderAPI, assignmentAPI } from '@/lib/api';
import { ChevronLeft, ChevronRight, RefreshCw, Zap, Calendar as CalendarIcon, Upload } from 'lucide-react';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { toast } from 'sonner';
import WorkOrderCard from './WorkOrderCard';
import EmployeeCalendarRow from './EmployeeCalendarRow';

export default function CalendarView({ user }) {
  const [view, setView] = useState('weekday'); // 'daily', 'weekday', 'week', 'rolling4', 'monthly'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [employees, setEmployees] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [employeeAvailability, setEmployeeAvailability] = useState({}); // {employee_id: {date: hours}}
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [unassignedOrders, setUnassignedOrders] = useState([]);
  const [pushing, setPushing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  const canEdit = user?.role === 'Administrator' || user?.role === 'Manager';
  const isAdmin = user?.role === 'Administrator';

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const fetchData = async () => {
    try {
      setLoading(true);
      const [empRes, woRes] = await Promise.all([
        employeeAPI.getAll(),
        workOrderAPI.getAll(),
      ]);

      setEmployees(empRes.data);
      setWorkOrders(woRes.data);

      // Calculate date range based on view - use wider range for rolling4/monthly
      let startDate, endDate;
      if (view === 'rolling4') {
        startDate = format(addDays(startOfWeek(currentDate), -7), 'yyyy-MM-dd');
        endDate = format(addDays(startOfWeek(currentDate), 27), 'yyyy-MM-dd');
      } else if (view === 'monthly') {
        startDate = format(startOfMonth(currentDate), 'yyyy-MM-dd');
        endDate = format(endOfMonth(currentDate), 'yyyy-MM-dd');
      } else {
        startDate = format(startOfWeek(currentDate), 'yyyy-MM-dd');
        endDate = format(addDays(startOfWeek(currentDate), 6), 'yyyy-MM-dd');
      }
      
      const assignRes = await assignmentAPI.getAll(startDate, endDate);
      setAssignments(assignRes.data);

      // Fetch employee availability for all employees in the date range
      const availabilityMap = {};
      await Promise.all(
        empRes.data.map(async (emp) => {
          try {
            const availRes = await employeeAPI.getAvailability(emp.id, startDate, endDate);
            availabilityMap[emp.id] = {};
            availRes.data.forEach((avail) => {
              availabilityMap[emp.id][avail.date] = avail.available_hours;
            });
          } catch (err) {
            // If error, just use default hours
            availabilityMap[emp.id] = {};
          }
        })
      );
      setEmployeeAvailability(availabilityMap);

      const assignedIds = new Set(assignRes.data.map((a) => a.work_order_id));
      setUnassignedOrders(woRes.data.filter((wo) => !assignedIds.has(wo.id)));
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentDate, view]);

  const handleSync = async () => {
    try {
      setSyncing(true);
      const response = await workOrderAPI.sync();
      
      // Sync now runs in background - poll for completion
      if (response.data.message && response.data.message.includes('background')) {
        toast.info('Sync started... This may take a minute.');
        
        // Poll for sync status
        const pollInterval = setInterval(async () => {
          try {
            const statusResponse = await workOrderAPI.getSyncStatus();
            const status = statusResponse.data;
            
            if (!status.in_progress) {
              clearInterval(pollInterval);
              setSyncing(false);
              
              if (status.error) {
                toast.error(`Sync failed: ${status.error}`);
              } else if (status.last_result) {
                const { synced, removed } = status.last_result;
                let message = `Synced ${synced || 0} work orders`;
                if (removed > 0) message += `, removed ${removed}`;
                toast.success(message);
              } else {
                toast.success('Sync completed');
              }
              fetchData();
            }
          } catch (err) {
            console.error('Error polling sync status:', err);
          }
        }, 3000); // Poll every 3 seconds
        
        // Timeout after 3 minutes
        setTimeout(() => {
          clearInterval(pollInterval);
          if (syncing) {
            setSyncing(false);
            toast.warning('Sync is taking longer than expected. Please refresh to see updates.');
            fetchData();
          }
        }, 180000);
      } else {
        // Legacy sync response (immediate completion)
        const { synced, removed, hours_updated } = response.data;
        let message = `Synced ${synced} work orders`;
        if (removed > 0) message += `, removed ${removed}`;
        if (hours_updated > 0) message += `, updated hours on ${hours_updated}`;
        toast.success(message);
        fetchData();
        setSyncing(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed');
      setSyncing(false);
    }
  };

  const handleAutoAssign = async () => {
    if (unassignedOrders.length === 0) {
      toast.info('No unassigned work orders');
      return;
    }

    try {
      const ids = unassignedOrders.map((wo) => wo.id);
      await assignmentAPI.autoAssign(ids);
      toast.success('Work orders auto-assigned');
      fetchData();
    } catch (error) {
      toast.error('Auto-assign failed');
    }
  };

  // Generate days based on view mode
  const getDaysForView = () => {
    if (view === 'daily') {
      return [currentDate];
    } else if (view === 'week') {
      // Full 7-day week including weekends
      return Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(currentDate), i));
    } else if (view === 'rolling4') {
      // Rolling 4 weeks: prior week + current week + next 2 weeks
      const priorWeekStart = addDays(startOfWeek(currentDate), -7);
      return Array.from({ length: 28 }, (_, i) => addDays(priorWeekStart, i));
    } else if (view === 'monthly') {
      // All days in the current month
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);
      return eachDayOfInterval({ start: monthStart, end: monthEnd });
    } else {
      // Weekday: Monday-Friday only
      return Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(currentDate), i))
        .filter((date) => date.getDay() !== 0 && date.getDay() !== 6);
    }
  };

  const weekDays = getDaysForView();
  
  // Determine navigation increment
  const getNavigationIncrement = () => {
    if (view === 'daily') return 1;
    if (view === 'rolling4') return 7; // Move by 1 week
    if (view === 'monthly') return 30; // Move by ~1 month
    return 7; // Default for week/weekday
  };

  const handleDragStart = (event) => {
    console.log('🔵 DnD: Drag started', { activeId: event.active.id, data: event.active.data });
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    console.log('🟢 DnD: Drag ended', { activeId: active?.id, overId: over?.id });
    setActiveId(null);

    if (!over) {
      console.log('🔴 DnD: No drop target');
      return;
    }

    // Parse the drop target (employee_id_date format)
    const [employeeId, dateStr] = over.id.split('_');
    
    // Check if we're moving an existing assignment or creating a new one
    const isMovingAssignment = active.id.toString().startsWith('assignment_');
    
    if (isMovingAssignment) {
      // Moving an existing assignment - use moveWorkOrder to handle the entire work order
      const assignmentId = active.id.replace('assignment_', '');
      const existingAssignment = assignments.find((a) => a.id === assignmentId);
      
      if (!existingAssignment) return;
      
      // Get the work order to find all related assignments
      const workOrderId = existingAssignment.work_order_id;
      
      // Check if this is dropping in the same location as the first assignment
      const workOrderAssignments = assignments
        .filter((a) => a.work_order_id === workOrderId)
        .sort((a, b) => a.date.localeCompare(b.date));
      
      const firstAssignment = workOrderAssignments[0];
      if (firstAssignment && firstAssignment.employee_id === employeeId && firstAssignment.date === dateStr) {
        return; // No change needed - dropping in same starting position
      }
      
      try {
        // Move all assignments for this work order with smart splitting
        const response = await assignmentAPI.moveWorkOrder({
          work_order_id: workOrderId,
          employee_id: employeeId,
          start_date: dateStr,
        });
        
        const { assignments: newAssignments, remaining } = response.data;
        
        if (newAssignments.length > 1) {
          toast.success(`Work order split across ${newAssignments.length} days`);
        } else {
          toast.success('Work order moved successfully');
        }
        
        if (remaining > 0) {
          toast.warning(`${remaining} hours could not be scheduled (insufficient capacity)`);
        }
        
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to move work order');
      }
    } else {
      // Creating a new assignment from unassigned work orders - use smart assign
      const workOrderId = active.id;
      const workOrder = workOrders.find((wo) => wo.id === workOrderId);
      if (!workOrder) return;

      try {
        // Use smart assign to automatically split across days if needed
        const response = await assignmentAPI.smartAssign({
          work_order_id: workOrderId,
          employee_id: employeeId,
          start_date: dateStr,
          total_hours: workOrder.man_hours,
        });
        
        const { assignments: newAssignments, remaining } = response.data;
        
        if (newAssignments.length > 1) {
          toast.success(`Work order split across ${newAssignments.length} days`);
        } else {
          toast.success('Work order assigned');
        }
        
        if (remaining > 0) {
          toast.warning(`${remaining} hours could not be scheduled (insufficient capacity)`);
        }
        
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Assignment failed');
      }
    }
  };

  // Get the active item (either a work order or an assignment being dragged)
  const getActiveItem = () => {
    if (!activeId) return null;
    
    if (activeId.toString().startsWith('assignment_')) {
      const assignmentId = activeId.replace('assignment_', '');
      const assignment = assignments.find((a) => a.id === assignmentId);
      if (assignment) {
        return workOrders.find((wo) => wo.id === assignment.work_order_id);
      }
    }
    return workOrders.find((wo) => wo.id === activeId);
  };
  
  const activeWorkOrder = getActiveItem();

  const handlePushToNetsuite = async () => {
    if (typeof window !== 'undefined' && !window.confirm('Push all scheduled start dates to NetSuite? This will update the start date field for all assigned work orders.')) {
      return;
    }
    
    try {
      setPushing(true);
      const response = await workOrderAPI.pushToNetsuite();
      const { updated, total, errors, message } = response.data;
      
      if (errors && errors.length > 0) {
        toast.warning(message);
        console.error('Push errors:', errors);
      } else {
        toast.success(message || `Successfully pushed ${updated} work orders to NetSuite`);
      }
      
      // Refresh data to show updated last_pushed timestamps
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to push to NetSuite');
    } finally {
      setPushing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-lg font-medium text-muted-foreground">Loading calendar...</div>
      </div>
    );
  }

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="h-full flex flex-col" data-testid="calendar-view">
        <div className="bg-white dark:bg-slate-900 border-b border-border p-6 backdrop-blur-xl bg-white/95">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-3xl font-bold tracking-tight">Production Schedule</h2>
              <p className="text-sm text-muted-foreground mt-1">
                {format(currentDate, 'MMMM yyyy')}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {canEdit && (
                <>
                  <Button 
                    variant="outline" 
                    onClick={handleSync} 
                    disabled={syncing}
                    data-testid="sync-button"
                  >
                    <RefreshCw className={`mr-2 h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                    {syncing ? 'Syncing...' : 'Sync NetSuite'}
                  </Button>
                  {isAdmin && (
                    <Button
                      variant="outline"
                      onClick={handlePushToNetsuite}
                      disabled={pushing}
                      className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                      data-testid="push-netsuite-button"
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      {pushing ? 'Pushing...' : 'Push to NetSuite'}
                    </Button>
                  )}
                  <Button
                    onClick={handleAutoAssign}
                    className="bg-amber-500 hover:bg-amber-600 text-white"
                    data-testid="auto-assign-button"
                  >
                    <Zap className="mr-2 h-4 w-4" />
                    Auto-Assign ({unassignedOrders.length})
                  </Button>
                </>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setCurrentDate(addDays(currentDate, -getNavigationIncrement()))}
                data-testid="prev-button"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <Button
                variant="outline"
                onClick={() => setCurrentDate(new Date())}
                data-testid="today-button"
              >
                Today
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setCurrentDate(addDays(currentDate, getNavigationIncrement()))}
                data-testid="next-button"
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>
          </div>
          
          <div className="flex items-center justify-center">
            <ToggleGroup type="single" value={view} onValueChange={(v) => v && setView(v)}>
              <ToggleGroupItem value="daily" data-testid="view-daily" className="px-4">
                <CalendarIcon className="mr-2 h-4 w-4" />
                Daily
              </ToggleGroupItem>
              <ToggleGroupItem value="weekday" data-testid="view-weekday" className="px-4">
                <CalendarIcon className="mr-2 h-4 w-4" />
                Weekday
              </ToggleGroupItem>
              <ToggleGroupItem value="week" data-testid="view-week" className="px-4">
                <CalendarIcon className="mr-2 h-4 w-4" />
                Week
              </ToggleGroupItem>
              <ToggleGroupItem value="rolling4" data-testid="view-rolling4" className="px-4">
                <CalendarIcon className="mr-2 h-4 w-4" />
                Rolling 4 Weeks
              </ToggleGroupItem>
              <ToggleGroupItem value="monthly" data-testid="view-monthly" className="px-4">
                <CalendarIcon className="mr-2 h-4 w-4" />
                Monthly
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-6">
          <div className="flex gap-6">
            <div className={`${canEdit ? 'flex-1' : 'w-full'}`}>
              <Card className="overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                <div className={`grid border-b border-border`} style={{ gridTemplateColumns: `200px repeat(${weekDays.length}, 1fr)` }}>
                  <div className="p-4 font-semibold bg-slate-50 dark:bg-slate-800 border-r border-border">
                    Employee
                  </div>
                  {weekDays.map((date) => {
                    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                    return (
                      <div
                        key={date.toISOString()}
                        className={`p-2 text-center border-r border-border ${
                          isWeekend && view === 'rolling4' 
                            ? 'bg-slate-100 dark:bg-slate-800/50' 
                            : 'bg-slate-50 dark:bg-slate-800'
                        }`}
                      >
                        <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">
                          {format(date, 'EEE')}
                        </div>
                        <div className={`${
                          view === 'rolling4' || view === 'monthly' 
                            ? 'text-sm' 
                            : 'text-lg'
                        } font-bold font-mono mt-0.5`}>
                          {format(date, 'd')}
                        </div>
                        {(view === 'daily' || view === 'monthly' || view === 'rolling4') && (
                          <div className="text-[10px] text-muted-foreground mt-0.5">
                            {format(date, 'MMM')}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {employees.map((employee) => (
                  <EmployeeCalendarRow
                    key={employee.id}
                    employee={employee}
                    weekDays={weekDays}
                    assignments={assignments.filter((a) => a.employee_id === employee.id)}
                    workOrders={workOrders}
                    onRefresh={fetchData}
                    canEdit={canEdit}
                    viewMode={view}
                    availability={employeeAvailability[employee.id] || {}}
                    isAdmin={user?.role === 'Administrator'}
                  />
                ))}
                </div>
              </Card>
            </div>

            {canEdit && (
              <div className="w-80">
                <Card className="p-5 sticky top-0 shadow-sm">
                  <h3 className="text-lg font-bold mb-4">Unassigned Orders</h3>
                  <div className="space-y-3 max-h-[calc(100vh-200px)] overflow-y-auto scrollbar-hide">
                    {unassignedOrders.map((wo) => (
                      <WorkOrderCard key={wo.id} workOrder={wo} isDraggable />
                    ))}
                    {unassignedOrders.length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-8">
                        All work orders assigned
                      </p>
                    )}
                  </div>
                </Card>
              </div>
            )}
          </div>
        </div>
      </div>

      <DragOverlay>
        {activeWorkOrder && (
          <div className="drag-overlay">
            <WorkOrderCard workOrder={activeWorkOrder} isDraggable={false} />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}
